import React, { useState, useRef, useEffect } from "react";
import { Doughnut, Line, Bar } from "react-chartjs-2";
import article from "./InvestoContent";
import WaldoAd from '../AdComponent';
import { 
  Chart, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
} from "chart.js";

Chart.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
); 

const LumpSumReturnsCalculator = () => {
  const content = article.find((item) => item.id === 4);
  const [activeTab, setActiveTab] = useState("calculator");
  const [isMobile, setIsMobile] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [calculationType, setCalculationType] = useState("future"); // future, present, rate

  const [inputs, setInputs] = useState({
    // Basic inputs
    initialInvestment: "10000",
    annualReturn: "8",
    investmentPeriod: "10",
    
    // Future value calculation
    futureValue: "",
    
    // Present value calculation  
    presentValue: "",
    
    // Rate calculation
    targetValue: "",
    
    // Advanced inputs
    compoundingFrequency: "1", // 1=annual, 4=quarterly, 12=monthly
    inflationRate: "2.5",
    taxRate: "15",
    
    // Additional contributions
    additionalContributions: "0",
    contributionFrequency: "yearly", // yearly, quarterly, monthly
    
    // Withdrawal settings
    withdrawalAmount: "0",
    withdrawalStart: "0",
    
    // Market conditions
    marketVolatility: "15",
    probability: "70",
    
    // Common inputs
    currency: "1",
    investmentType: "stocks" // stocks, bonds, real_estate, mutual_funds
  });

  const [scenarios, setScenarios] = useState([
    { return: "5", probability: "20", label: "Conservative" },
    { return: "8", probability: "50", label: "Moderate" },
    { return: "12", probability: "30", label: "Aggressive" }
  ]);

  const [result, setResult] = useState(null);
  const [calculationHistory, setCalculationHistory] = useState([]);

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (activeTab === "calculator") {
      calculateReturns();
    }
  }, [inputs, scenarios, calculationType, activeTab]);

  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  const handleChange = (e) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
  };

  const handleScenarioChange = (index, field, value) => {
    const updatedScenarios = scenarios.map((scenario, i) => 
      i === index ? { ...scenario, [field]: value } : scenario
    );
    setScenarios(updatedScenarios);
  };

  const addScenario = () => {
    setScenarios([...scenarios, { return: "10", probability: "25", label: "Custom" }]);
  };

  const removeScenario = (index) => {
    if (scenarios.length > 1) {
      setScenarios(scenarios.filter((_, i) => i !== index));
    }
  };

  const calculateReturns = () => {
    const currencyRate = parseFloat(inputs.currency) || 1;
    let initialInvestment = parseFloat(inputs.initialInvestment) || 0;
    const annualReturn = parseFloat(inputs.annualReturn) || 0;
    const investmentPeriod = parseFloat(inputs.investmentPeriod) || 0;
    const compoundingFrequency = parseFloat(inputs.compoundingFrequency) || 1;
    const inflationRate = parseFloat(inputs.inflationRate) || 0;
    const taxRate = parseFloat(inputs.taxRate) || 0;
    const additionalContributions = parseFloat(inputs.additionalContributions) || 0;
    const withdrawalAmount = parseFloat(inputs.withdrawalAmount) || 0;
    const withdrawalStart = parseFloat(inputs.withdrawalStart) || 0;
    const marketVolatility = parseFloat(inputs.marketVolatility) || 0;

    let futureValue = 0;
    let presentValue = 0;
    let requiredRate = 0;
    let calculationDetails = "";

    // Calculate based on calculation type
    switch (calculationType) {
      case "future":
        // Future Value = PV * (1 + r/n)^(n*t)
        const ratePerPeriod = annualReturn / 100 / compoundingFrequency;
        const totalPeriods = investmentPeriod * compoundingFrequency;
        
        futureValue = initialInvestment * Math.pow(1 + ratePerPeriod, totalPeriods);
        
        // Add additional contributions
        if (additionalContributions > 0) {
          const contributionMultiplier = getContributionMultiplier();
          futureValue += additionalContributions * contributionMultiplier;
        }
        
        // Subtract withdrawals
        if (withdrawalAmount > 0 && withdrawalStart > 0) {
          const withdrawalPeriods = (investmentPeriod - withdrawalStart) * compoundingFrequency;
          if (withdrawalPeriods > 0) {
            const withdrawalMultiplier = getWithdrawalMultiplier();
            futureValue -= withdrawalAmount * withdrawalMultiplier;
          }
        }
        
        calculationDetails = `Future value of $${initialInvestment} at ${annualReturn}% for ${investmentPeriod} years`;
        break;

      case "present":
      // Present Value = FV / (1 + r/n)^(n*t)
      presentValue = parseFloat(inputs.presentValue) || 0;
      const pvRatePerPeriod = annualReturn / 100 / compoundingFrequency;
      const pvTotalPeriods = investmentPeriod * compoundingFrequency;
      
      initialInvestment = presentValue / Math.pow(1 + pvRatePerPeriod, pvTotalPeriods);
      calculationDetails = `Present value needed for $${presentValue} at ${annualReturn}% for ${investmentPeriod} years`;
      break;

      case "rate":
      // Required Rate = (FV/PV)^(1/t) - 1
      const targetValue = parseFloat(inputs.targetValue) || 0;
      requiredRate = (Math.pow(targetValue / initialInvestment, 1 / investmentPeriod) - 1) * 100;
      calculationDetails = `Required rate to grow $${initialInvestment} to $${targetValue} in ${investmentPeriod} years`;
      break;
    }

    // Adjust for inflation
    const inflationAdjustedReturn = ((1 + annualReturn/100) / (1 + inflationRate/100) - 1) * 100;
    const realFutureValue = futureValue / Math.pow(1 + inflationRate/100, investmentPeriod);

    // Calculate tax impact
    const taxableGain = futureValue - initialInvestment;
    const afterTaxGain = taxableGain * (1 - taxRate/100);
    const afterTaxValue = initialInvestment + afterTaxGain;

    // Calculate compound annual growth rate (CAGR)
    const cagr = (Math.pow(futureValue / initialInvestment, 1 / investmentPeriod) - 1) * 100;

    // Calculate wealth breakdown
    const totalContributions = initialInvestment + (additionalContributions * investmentPeriod);
    const totalEarnings = futureValue - totalContributions;

    // Calculate volatility impact
    const bestCaseReturn = annualReturn + marketVolatility;
    const worstCaseReturn = Math.max(0, annualReturn - marketVolatility);
    const bestCaseValue = initialInvestment * Math.pow(1 + bestCaseReturn/100, investmentPeriod);
    const worstCaseValue = initialInvestment * Math.pow(1 + worstCaseReturn/100, investmentPeriod);

    const resultData = {
      // Core calculations
      futureValue: futureValue * currencyRate,
      presentValue: presentValue * currencyRate,
      requiredRate,
      initialInvestment: initialInvestment * currencyRate,
      annualReturn,
      investmentPeriod,
      
      // Adjusted values
      realFutureValue: realFutureValue * currencyRate,
      afterTaxValue: afterTaxValue * currencyRate,
      inflationAdjustedReturn,
      cagr,
      
      // Breakdown
      totalContributions: totalContributions * currencyRate,
      totalEarnings: totalEarnings * currencyRate,
      taxableGain: taxableGain * currencyRate,
      taxPaid: (taxableGain * taxRate/100) * currencyRate,
      
      // Risk analysis
      bestCaseValue: bestCaseValue * currencyRate,
      worstCaseValue: worstCaseValue * currencyRate,
      marketVolatility,
      
      // Additional details
      compoundingFrequency,
      currencyRate,
      calculationType,
      calculationDetails,
      
      // Withdrawal analysis
      withdrawalAmount: withdrawalAmount * currencyRate,
      withdrawalStart,
      hasWithdrawals: withdrawalAmount > 0
    };

    setResult(resultData);
  };

  const getContributionMultiplier = () => {
    const annualReturn = parseFloat(inputs.annualReturn) || 0;
    const investmentPeriod = parseFloat(inputs.investmentPeriod) || 0;
    const compoundingFrequency = parseFloat(inputs.compoundingFrequency) || 1;
    const contributionFrequency = inputs.contributionFrequency;
    
    const ratePerPeriod = annualReturn / 100 / compoundingFrequency;
    const totalPeriods = investmentPeriod * compoundingFrequency;
    
    let contributionPeriods = 0;
    switch (contributionFrequency) {
      case "yearly":
        contributionPeriods = investmentPeriod;
        break;
      case "quarterly":
        contributionPeriods = investmentPeriod * 4;
        break;
      case "monthly":
        contributionPeriods = investmentPeriod * 12;
        break;
    }
    
    // Future value of annuity formula
    return (Math.pow(1 + ratePerPeriod, contributionPeriods) - 1) / ratePerPeriod;
  };

  const getWithdrawalMultiplier = () => {
    const annualReturn = parseFloat(inputs.annualReturn) || 0;
    const investmentPeriod = parseFloat(inputs.investmentPeriod) || 0;
    const withdrawalStart = parseFloat(inputs.withdrawalStart) || 0;
    const compoundingFrequency = parseFloat(inputs.compoundingFrequency) || 1;
    
    const ratePerPeriod = annualReturn / 100 / compoundingFrequency;
    const withdrawalPeriods = (investmentPeriod - withdrawalStart) * compoundingFrequency;
    
    // Present value of annuity formula
    return (1 - Math.pow(1 + ratePerPeriod, -withdrawalPeriods)) / ratePerPeriod;
  };

  const calculateYearlyProjection = () => {
    if (!result) return [];

    const projection = [];
    const annualReturn = parseFloat(inputs.annualReturn) || 0;
    const additionalContributions = parseFloat(inputs.additionalContributions) || 0;
    const withdrawalAmount = parseFloat(inputs.withdrawalAmount) || 0;
    const withdrawalStart = parseFloat(inputs.withdrawalStart) || 0;
    
    let currentValue = result.initialInvestment;

    for (let year = 0; year <= result.investmentPeriod; year++) {
      if (year > 0) {
        // Apply return
        currentValue = currentValue * (1 + annualReturn/100);
        
        // Add contributions
        if (additionalContributions > 0) {
          currentValue += additionalContributions;
        }
        
        // Subtract withdrawals
        if (withdrawalAmount > 0 && year >= withdrawalStart) {
          currentValue = Math.max(0, currentValue - withdrawalAmount);
        }
      }

      const contributions = result.initialInvestment + (additionalContributions * year);
      const earnings = currentValue - contributions;
      
      projection.push({
        year,
        value: currentValue,
        contributions,
        earnings,
        hasWithdrawal: year >= withdrawalStart && withdrawalAmount > 0
      });
    }

    return projection;
  };

  const calculateScenarioAnalysis = () => {
    return scenarios.map(scenario => {
      const returnRate = parseFloat(scenario.return) || 0;
      const probability = parseFloat(scenario.probability) || 0;
      const initialInvestment = parseFloat(inputs.initialInvestment) || 0;
      const investmentPeriod = parseFloat(inputs.investmentPeriod) || 0;
      
      const futureValue = initialInvestment * Math.pow(1 + returnRate/100, investmentPeriod);
      const totalReturn = futureValue - initialInvestment;
      const annualizedReturn = (Math.pow(futureValue / initialInvestment, 1 / investmentPeriod) - 1) * 100;
      const expectedValue = (futureValue * probability) / 100;
      
      return {
        ...scenario,
        futureValue,
        totalReturn,
        annualizedReturn,
        expectedValue,
        probabilityWeight: probability / 100
      };
    });
  };

  const calculateInflationAnalysis = () => {
    if (!result) return [];

    const inflationRates = [0, 2, 3, 4, 5, 6];
    
    return inflationRates.map(rate => {
      const realValue = result.futureValue / Math.pow(1 + rate/100, result.investmentPeriod);
      const purchasingPower = (realValue / result.futureValue) * 100;
      
      return {
        inflationRate: rate,
        realValue,
        purchasingPower,
        lossPercentage: 100 - purchasingPower
      };
    });
  };

  const resetCalculator = () => {
    setScenarios([
      { return: "5", probability: "20", label: "Conservative" },
      { return: "8", probability: "50", label: "Moderate" },
      { return: "12", probability: "30", label: "Aggressive" }
    ]);
    setInputs({
      initialInvestment: "10000",
      annualReturn: "8",
      investmentPeriod: "10",
      futureValue: "",
      presentValue: "",
      targetValue: "",
      compoundingFrequency: "1",
      inflationRate: "2.5",
      taxRate: "15",
      additionalContributions: "0",
      contributionFrequency: "yearly",
      withdrawalAmount: "0",
      withdrawalStart: "0",
      marketVolatility: "15",
      probability: "70",
      currency: "1",
      investmentType: "stocks"
    });
    addNotification("Calculator reset to default values", "success");
  };

  const exportToCSV = () => {
    if (!result) return;
    
    const csvContent = [
      "Metric,Value",
      `Future Value,${result.futureValue.toFixed(2)}`,
      `Initial Investment,${result.initialInvestment.toFixed(2)}`,
      `Total Contributions,${result.totalContributions.toFixed(2)}`,
      `Total Earnings,${result.totalEarnings.toFixed(2)}`,
      `Annual Return,${result.annualReturn}%`,
      `Investment Period,${result.investmentPeriod} years`,
      `CAGR,${result.cagr.toFixed(2)}%`,
      `Real Future Value,${result.realFutureValue.toFixed(2)}`,
      `After-Tax Value,${result.afterTaxValue.toFixed(2)}`,
      `Tax Paid,${result.taxPaid.toFixed(2)}`,
      `Best Case Value,${result.bestCaseValue.toFixed(2)}`,
      `Worst Case Value,${result.worstCaseValue.toFixed(2)}`,
      `Calculation Type,${result.calculationType}`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lump-sum-returns.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    addNotification("Data exported to CSV successfully", "success");
  };

  const printResult = () => {
    window.print();
  };

  const saveToHistory = () => {
    if (!result) return;
    
    const newCalculation = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      inputs: { ...inputs },
      scenarios: [...scenarios],
      calculationType,
      ...result
    };
    
    setCalculationHistory(prev => [newCalculation, ...prev.slice(0, 9)]);
    addNotification("Calculation saved to history", "success");
  };

  const loadCalculation = (calc) => {
    setInputs(calc.inputs);
    setScenarios(calc.scenarios || []);
    setCalculationType(calc.calculationType || "future");
    setResult(calc.result);
    setShowSaved(false);
    addNotification("Calculation loaded successfully", "success");
  };

  const quickPreset = (preset) => {
    const presets = {
      conservative: { 
        calculationType: "future",
        inputs: { 
          initialInvestment: "5000", 
          annualReturn: "6",
          investmentPeriod: "15",
          inflationRate: "2.5",
          taxRate: "15"
        }
      },
      moderate: { 
        calculationType: "future",
        inputs: { 
          initialInvestment: "10000", 
          annualReturn: "8",
          investmentPeriod: "10",
          inflationRate: "2.5",
          taxRate: "20"
        }
      },
      aggressive: { 
        calculationType: "future",
        inputs: { 
          initialInvestment: "25000", 
          annualReturn: "12",
          investmentPeriod: "7",
          inflationRate: "3",
          taxRate: "25"
        }
      }
    };
    
    const presetData = presets[preset];
    setCalculationType(presetData.calculationType);
    setInputs(prev => ({ ...prev, ...presetData.inputs }));
    addNotification(`${preset} preset applied`, "info");
  };

  // Chart Data
  const wealthChartData = result ? {
    labels: ["Initial Investment", "Total Earnings"],
    datasets: [{
      data: [result.initialInvestment, result.totalEarnings],
      backgroundColor: ["#19692C", "#FFD93D"],
      borderWidth: 2,
      hoverOffset: 15
    }],
  } : null;

  const projectionData = calculateYearlyProjection();
  const projectionChartData = projectionData.length > 0 ? {
    labels: projectionData.map(d => `Year ${d.year}`),
    datasets: [
      {
        label: 'Portfolio Value',
        data: projectionData.map(d => d.value),
        borderColor: "#19692C",
        backgroundColor: "rgba(25, 105, 44, 0.1)",
        fill: true,
        tension: 0.4
      },
      {
        label: 'Total Contributions',
        data: projectionData.map(d => d.contributions),
        borderColor: "#FFD93D",
        borderDash: [5, 5],
        fill: false,
        tension: 0.4
      }
    ]
  } : null;

  const scenarioData = calculateScenarioAnalysis();
  const scenarioChartData = scenarioData.length > 0 ? {
    labels: scenarioData.map(s => s.label),
    datasets: [
      {
        label: 'Future Value',
        data: scenarioData.map(s => s.futureValue),
        backgroundColor: "#19692C",
        borderColor: "#19692C",
        borderWidth: 2
      },
      {
        label: 'Expected Value',
        data: scenarioData.map(s => s.expectedValue),
        backgroundColor: "#FFD93D",
        borderColor: "#FFD93D",
        borderWidth: 2
      }
    ]
  } : null;

  const inflationData = calculateInflationAnalysis();
  const inflationChartData = inflationData.length > 0 ? {
    labels: inflationData.map(i => `${i.inflationRate}%`),
    datasets: [{
      label: 'Real Value',
      data: inflationData.map(i => i.realValue),
      backgroundColor: inflationData.map(i => 
        i.inflationRate <= 2 ? '#28A745' :
        i.inflationRate <= 4 ? '#FFD93D' : '#E53E3E'
      ),
      borderColor: inflationData.map(i => 
        i.inflationRate <= 2 ? '#28A745' :
        i.inflationRate <= 4 ? '#FFD93D' : '#E53E3E'
      ),
      borderWidth: 2
    }]
  } : null;

  return (
    <div className={'profit-calculator'}>
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            <i className={`fas fa-${notif.type === 'success' ? 'check' : 'info'}-circle`}></i>
            {notif.message}
          </div>
        ))}
      </div>

      <div className="calculator-header">
        <div className="header-top">
          <h1>Lump Sum Returns Calculator</h1>
        </div>
        <p>Calculate future value, present value requirements, and analyze investment growth with compound interest</p>
      </div>
<WaldoAd type="horizontal" /><br/>
      {/* Saved Calculations Modal */}
      {showSaved && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Saved Calculations</h3>
              <button className="close-btn" onClick={() => setShowSaved(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="saved-list">
              {savedCalculations.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-bookmark fa-2x"></i>
                  <p>No saved calculations yet</p>
                </div>
              ) : (
                savedCalculations.map(calc => (
                  <div key={calc.id} className="saved-item" onClick={() => loadCalculation(calc)}>
                    <div className="saved-name">{calc.name}</div>
                    <div className="saved-details">
                      <span className="profit positive">
                        ${calc.futureValue?.toFixed(2)}
                      </span>
                      <span className="date">{calc.timestamp}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <div className="calculator-layout">
        {/* Left Side - Inputs */}
        <div className="left-panel">
          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === "calculator" ? "active" : ""}`}
                onClick={() => setActiveTab("calculator")}
              >
                <i className="fas fa-calculator"></i>
                {isMobile ? 'Calculator' : 'Returns Calc'}
              </button>
              <button 
                className={`tab ${activeTab === "scenarios" ? "active" : ""}`}
                onClick={() => setActiveTab("scenarios")}
              >
                <i className="fas fa-chart-bar"></i>
                {isMobile ? 'Scenarios' : 'Multi-Scenario'}
              </button>
              <button 
                className={`tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                <i className="fas fa-history"></i>
                {isMobile ? 'History' : 'History'}
              </button>
            </div>
          </div>

          {/* Quick Presets */}
          <div className="presets-section">
            <h4>Investment Strategy:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => quickPreset('conservative')}>
                Conservative
              </button>
              <button className="preset-btn moderate" onClick={() => quickPreset('moderate')}>
                Moderate
              </button>
              <button className="preset-btn aggressive" onClick={() => quickPreset('aggressive')}>
                Aggressive
              </button>
            </div>
          </div>

          {/* Calculation Type Selection */}
          <div className="mode-selection">
            <div className="mode-buttons">
              <button 
                className={`mode-btn ${calculationType === "future" ? "active" : ""}`}
                onClick={() => setCalculationType("future")}
              >
                <i className="fas fa-chart-line"></i>
                Future Value
              </button>
              <button 
                className={`mode-btn ${calculationType === "present" ? "active" : ""}`}
                onClick={() => setCalculationType("present")}
              >
                <i className="fas fa-search-dollar"></i>
                Present Value
              </button>
              <button 
                className={`mode-btn ${calculationType === "rate" ? "active" : ""}`}
                onClick={() => setCalculationType("rate")}
              >
                <i className="fas fa-percentage"></i>
                Required Rate
              </button>
            </div>
          </div>

          {/* Returns Calculator Tab */}
          {activeTab === "calculator" && (
            <div className="calculator-content">
              <div className="input-section">
                <div className="input-group">
                  {/* Basic Investment Inputs */}
                  <h3>
                    <i className="fas fa-seedling"></i>
                    Investment Details
                  </h3>
                  <div className="input-row">
                    {calculationType === "future" && (
                      <div className="input-field">
                        <label><i className="fa-solid fa-dollar-sign"></i> Initial Investment</label>
                        <input 
                          type="number" 
                          name="initialInvestment" 
                          value={inputs.initialInvestment} 
                          onChange={handleChange}
                          placeholder="10000"
                        />
                      </div>
                    )}
                    
                    {calculationType === "present" && (
                      <div className="input-field">
                        <label><i className="fa-solid fa-bullseye"></i> Target Future Value</label>
                        <input 
                          type="number" 
                          name="presentValue" 
                          value={inputs.presentValue} 
                          onChange={handleChange}
                          placeholder="50000"
                        />
                      </div>
                    )}
                    
                    {calculationType === "rate" && (
                      <div className="input-field">
                        <label><i className="fa-solid fa-bullseye"></i> Target Future Value</label>
                        <input 
                          type="number" 
                          name="targetValue" 
                          value={inputs.targetValue} 
                          onChange={handleChange}
                          placeholder="50000"
                        />
                      </div>
                    )}
                    
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Annual Return (%)</label>
                      <input 
                        type="number" 
                        name="annualReturn" 
                        value={inputs.annualReturn} 
                        onChange={handleChange}
                        placeholder="8"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-calendar"></i> Investment Period (Years)</label>
                      <input 
                        type="number" 
                        name="investmentPeriod" 
                        value={inputs.investmentPeriod} 
                        onChange={handleChange}
                        placeholder="10"
                      />
                    </div>
                  </div>

                  {/* Additional Contributions */}
                  <h3>
                    <i className="fas fa-plus-circle"></i>
                    Additional Contributions
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Additional Amount</label>
                      <input 
                        type="number" 
                        name="additionalContributions" 
                        value={inputs.additionalContributions} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-calendar"></i> Contribution Frequency</label>
                      <select name="contributionFrequency" value={inputs.contributionFrequency} onChange={handleChange}>
                        <option value="yearly">Yearly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>
                  </div>

                  {/* Withdrawal Settings */}
                  <h3>
                    <i className="fas fa-minus-circle"></i>
                    Withdrawal Settings
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Withdrawal Amount</label>
                      <input 
                        type="number" 
                        name="withdrawalAmount" 
                        value={inputs.withdrawalAmount} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-calendar"></i> Start Withdrawal After (Years)</label>
                      <input 
                        type="number" 
                        name="withdrawalStart" 
                        value={inputs.withdrawalStart} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                  </div>

                  {/* Advanced Settings */}
                  <h3>
                    <i className="fas fa-sliders-h"></i>
                    Advanced Settings
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-calculator"></i> Compounding Frequency</label>
                      <select name="compoundingFrequency" value={inputs.compoundingFrequency} onChange={handleChange}>
                        <option value="1">Annually</option>
                        <option value="2">Semi-Annually</option>
                        <option value="4">Quarterly</option>
                        <option value="12">Monthly</option>
                        <option value="365">Daily</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-chart-line"></i> Investment Type</label>
                      <select name="investmentType" value={inputs.investmentType} onChange={handleChange}>
                        <option value="stocks">Stocks</option>
                        <option value="bonds">Bonds</option>
                        <option value="real_estate">Real Estate</option>
                        <option value="mutual_funds">Mutual Funds</option>
                        <option value="etfs">ETFs</option>
                        <option value="crypto">Cryptocurrency</option>
                      </select>
                    </div>
                  </div>

                  {/* Economic Factors */}
                  <h3>
                    <i className="fas fa-chart-area"></i>
                    Economic Factors
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Inflation Rate (%)</label>
                      <input 
                        type="number" 
                        name="inflationRate" 
                        value={inputs.inflationRate} 
                        onChange={handleChange}
                        placeholder="2.5"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Tax Rate (%)</label>
                      <input 
                        type="number" 
                        name="taxRate" 
                        value={inputs.taxRate} 
                        onChange={handleChange}
                        placeholder="15"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-bolt"></i> Market Volatility (%)</label>
                      <input 
                        type="number" 
                        name="marketVolatility" 
                        value={inputs.marketVolatility} 
                        onChange={handleChange}
                        placeholder="15"
                        step="0.1"
                      />
                    </div>
                  </div>

                  {/* Settings */}
                  <h3>
                    <i className="fas fa-cog"></i>
                    Settings
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-globe"></i> Currency Rate</label>
                      <input 
                        type="number" 
                        name="currency" 
                        value={inputs.currency} 
                        onChange={handleChange}
                        placeholder="1.00"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button className="btn reset-btn" onClick={resetCalculator}>
                  <i className="fas fa-redo"></i>
                  Reset
                </button>
                <button className="btn save-btn" onClick={saveToHistory}>
                  <i className="fas fa-save"></i>
                  Save to History
                </button>
              </div>
            </div>
          )}
<WaldoAd type="square" />
          {/* Multi-Scenario Tab */}
          {activeTab === "scenarios" && (
            <div className="projection-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-chess-board"></i>
                  Return Scenarios
                </h3>
                
                {scenarios.map((scenario, index) => (
                  <div key={index} className="purchase-group">
                    <br/><div className="purchase-header">
                      <h4>{scenario.label} Scenario</h4>
                      {scenarios.length > 1 && (
                        <button 
                          className="remove-purchase-btn"
                          onClick={() => removeScenario(index)}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      )}
                    </div><br/>
                    <div className="input-row">
                      <div className="input-field">
                        <label><i className="fa-solid fa-percentage"></i> Expected Return (%)</label>
                        <input 
                          type="number" 
                          value={scenario.return}
                          onChange={(e) => handleScenarioChange(index, 'return', e.target.value)}
                          placeholder="8"
                          step="0.1"
                        />
                      </div>
                      <div className="input-field">
                        <label><i className="fa-solid fa-chart-pie"></i> Probability (%)</label>
                        <input 
                          type="number" 
                          value={scenario.probability}
                          onChange={(e) => handleScenarioChange(index, 'probability', e.target.value)}
                          placeholder="50"
                          step="1"
                          min="0"
                          max="100"
                        />
                      </div>
                      <div className="input-field">
                        <label><i className="fa-solid fa-tag"></i> Scenario Label</label>
                        <input 
                          type="text" 
                          value={scenario.label}
                          onChange={(e) => handleScenarioChange(index, 'label', e.target.value)}
                          placeholder="Scenario Name"
                        />
                      </div>
                    </div><br/>
                  </div>
                ))}

                <button className="btn add-purchase-btn" onClick={addScenario}>
                  <i className="fas fa-plus"></i>
                  Add Another Scenario
                </button>

                <div className="constraints-info">
                  <h4>About Scenarios</h4>
                  <div className="info-list">
                    <div className="info-item">
                      <i className="fas fa-info-circle"></i>
                      <span><strong>Conservative:</strong> Lower returns with higher probability</span>
                    </div>
                    <div className="info-item">
                      <i className="fas fa-info-circle"></i>
                      <span><strong>Moderate:</strong> Balanced returns and probability</span>
                    </div>
                    <div className="info-item">
                      <i className="fas fa-info-circle"></i>
                      <span><strong>Aggressive:</strong> Higher returns with lower probability</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-history"></i>
                Recent Calculations
              </h3>
              {calculationHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <p>No calculations yet. Start calculating to see your history here.</p>
                </div>
              ) : (
                <div className="history-list">
                  {calculationHistory.map(calc => (
                    <div key={calc.id} className="history-item">
                      <div className="history-header">
                        <span className="history-date">
                          <i className="fas fa-clock"></i>
                          {calc.timestamp}
                        </span>
                        <span className="history-profit positive">
                          <i className="fas fa-chart-line"></i>
                          ${calc.futureValue?.toFixed(2)}
                        </span> 
                      </div>
                      <div className="history-details">
                        <span><i className="fas fa-dollar-sign"></i> Initial: ${calc.initialInvestment?.toFixed(2)}</span>
                        <span><i className="fas fa-percentage"></i> Return: {calc.annualReturn}%</span>
                        <span><i className="fas fa-calendar"></i> Years: {calc.investmentPeriod}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {(activeTab === "calculator" || activeTab === "scenarios") && (
          <div className="right-panel">
            {activeTab === "calculator" && (
              <div className="results-section">
                <div className="results-header">
                  <h2>
                    <i className="fas fa-chart-line"></i>
                    Investment Returns Analysis
                  </h2>
                  <div className="result-actions">
                    <button className="btn export-btn" onClick={exportToCSV}>
                      <i className="fas fa-file-export"></i>
                      {isMobile ? 'CSV' : 'Export CSV'}
                    </button>
                    <button className="btn print-btn" onClick={printResult}>
                      <i className="fas fa-print"></i>
                      {isMobile ? 'Print' : 'Print'}
                    </button>
                  </div>
                </div>
<WaldoAd type="square" />
                {result ? (
                  <>
                    <div className="results-grid">
                      <div className="result-card highlight">
                        <div className="result-value positive">
                          ${result.futureValue.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-bullseye"></i>
                          Future Value
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          ${result.initialInvestment.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-seedling"></i>
                          Initial Investment
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value positive">
                          ${result.totalEarnings.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-money-bill-wave"></i>
                          Total Earnings
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.cagr.toFixed(2)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-percentage"></i>
                          Annualized Return
                        </div>
                      </div>
                    </div>

                    <div className="detailed-results">
                      <div className="detail-row">
                        <span><i className="fas fa-calendar"></i> Investment Period:</span>
                        <span>{result.investmentPeriod} years</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-percentage"></i> Annual Return:</span>
                        <span>{result.annualReturn}%</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-dollar-sign"></i> Total Contributions:</span>
                        <span>${result.totalContributions.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-calculator"></i> Compounding Frequency:</span>
                        <span>
                          {result.compoundingFrequency === "1" ? "Annually" :
                           result.compoundingFrequency === "2" ? "Semi-Annually" :
                           result.compoundingFrequency === "4" ? "Quarterly" :
                           result.compoundingFrequency === "12" ? "Monthly" : "Daily"}
                        </span>
                      </div>
                      {result.hasWithdrawals && (
                        <>
                          <div className="detail-row">
                            <span><i className="fas fa-minus-circle"></i> Withdrawal Amount:</span>
                            <span>${result.withdrawalAmount.toFixed(2)}</span>
                          </div>
                          <div className="detail-row">
                            <span><i className="fas fa-calendar"></i> Withdrawal Start:</span>
                            <span>After {result.withdrawalStart} years</span>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Adjusted Values Summary */}
                    <div className="risk-summary">
                      <h4>Adjusted Values</h4>
                      <div className="risk-cards">
                        <div className="risk-card">
                          <div className="risk-value">
                            ${result.realFutureValue.toFixed(2)}
                          </div>
                          <div className="risk-label">Inflation-Adjusted</div>
                        </div>
                        <div className="risk-card">
                          <div className="risk-value">
                            ${result.afterTaxValue.toFixed(2)}
                          </div>
                          <div className="risk-label">After-Tax Value</div>
                        </div>
                        <div className="risk-card">
                          <div className="risk-value">
                            {result.inflationAdjustedReturn.toFixed(2)}%
                          </div>
                          <div className="risk-label">Real Return</div>
                        </div>
                      </div>
                    </div>

                    {/* Risk Analysis */}
                    <div className="risk-summary">
                      <h4>Risk Analysis</h4>
                      <div className="risk-cards">
                        <div className="risk-card">
                          <div className="risk-value positive">
                            ${result.bestCaseValue.toFixed(2)}
                          </div>
                          <div className="risk-label">Best Case</div>
                        </div>
                        <div className="risk-card">
                          <div className="risk-value">
                            ${result.futureValue.toFixed(2)}
                          </div>
                          <div className="risk-label">Expected Case</div>
                        </div>
                        <div className="risk-card">
                          <div className="risk-value negative">
                            ${result.worstCaseValue.toFixed(2)}
                          </div>
                          <div className="risk-label">Worst Case</div>
                        </div>
                      </div>
                    </div>

                    {/* Wealth Breakdown Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-pie"></i>
                        Wealth Composition
                      </h3>
                      <Doughnut 
                        data={wealthChartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              position: isMobile ? 'top' : 'bottom'
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  return `$${context.parsed.toFixed(2)}`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Projection Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-line"></i>
                        Growth Projection
                      </h3>
                      <Line 
                        data={projectionChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              display: true,
                              position: 'top'
                            }
                          },
                          scales: {
                            y: {
                              beginAtZero: true,
                              ticks: {
                                callback: function(value) {
                                  return '$' + value.toLocaleString();
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Inflation Impact Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-bar"></i>
                        Inflation Impact Analysis
                      </h3>
                      <Bar 
                        data={inflationChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              display: false
                            }
                          },
                          scales: {
                            y: {
                              ticks: {
                                callback: function(value) {
                                  return '$' + value.toLocaleString();
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Detailed Yearly Projection */}
                    <div className="risk-summary">
                      <h4>Yearly Projection</h4>
                      <div className="tips-list">
                        <div className="tip-item positive">
                          <span>Year</span>
                          <span>Portfolio Value</span>
                          <span>Contributions</span>
                          <span>Earnings</span>
                        </div>
                        {projectionData.filter((_, index) => index % Math.ceil(projectionData.length / 10) === 0 || index === projectionData.length - 1).map((year, index) => (
                          <div key={index} className={`tip-item positive ${year.hasWithdrawal ? 'with-withdrawal' : ''}`}>
                            <span>{year.year}</span>
                            <span>${year.value.toFixed(2)}</span>
                            <span>${year.contributions.toFixed(2)}</span>
                            <span className={year.earnings >= 0 ? 'positive' : 'negative'}>
                              ${year.earnings.toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-chart-line fa-3x"></i>
                    <p>Enter your investment details to calculate returns</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "scenarios" && result && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-chess-board"></i>
                  Multi-Scenario Analysis
                </h3>
                <div className="chart-container">
                  <Bar 
                    data={scenarioChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          display: true,
                          position: 'top'
                        }
                      },
                      scales: {
                        y: {
                          ticks: {
                            callback: function(value) {
                              return '$' + value.toLocaleString();
                            }
                          }
                        }
                      }
                    }}
                  />
                </div>
                
                <div className="scenario-list">
                  {scenarioData.map((scenario, index) => (
                    <div key={index} className="constraints-info">
                      <div className="scenario-header">
                        <span className="scenario-name">{scenario.label}</span>
                        <span className="scenario-return"> {scenario.return}% Return</span>
                      </div>
                      <div className="scenario-details">
                        <div className="scenario-metric">
                          <span>Future Value: </span>
                          <span>${scenario.futureValue.toFixed(2)}</span>
                        </div>
                        <div className="scenario-metric">
                          <span>Total Return: </span>
                          <span>${scenario.totalReturn.toFixed(2)}</span>
                        </div>
                        <div className="scenario-metric">
                          <span>Annualized Return: </span>
                          <span>{scenario.annualizedReturn.toFixed(2)}%</span>
                        </div>
                        <div className="scenario-metric">
                          <span>Probability: </span>
                          <span>{scenario.probability}%</span>
                        </div>
                        <div className="scenario-metric">
                          <span>Expected Value: </span>
                          <span>${scenario.expectedValue.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="projection-summary">
                  <div className="summary-card">
                    <div className="summary-value">
                      ${scenarioData.reduce((sum, s) => sum + s.expectedValue, 0).toFixed(2)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-balance-scale"></i>
                      Total Expected Value
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      {scenarioData.reduce((sum, s) => sum + parseFloat(s.probability), 0)}%
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-chart-pie"></i>
                      Total Probability
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      {(
                        scenarioData.reduce((sum, s) => sum + s.expectedValue, 0) / 
                        scenarioData.length
                      ).toFixed(2)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-calculator"></i>
                      Average Expected Value
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      {Math.max(...scenarioData.map(s => s.futureValue)).toFixed(2)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-arrow-up"></i>
                      Highest Scenario
                    </div>
                  </div><br/>
                </div>

                <div className="investment-tips">
                  <h4>Investment Insights</h4><br/>
                  <div className="tips-list">
                    <div className="tip-item positive">
                      <i className="fas fa-lightbulb"></i>
                      <span>Diversify your investments across different risk scenarios</span>
                    </div>
                    <div className="tip-item positive">
                      <i className="fas fa-lightbulb"></i>
                      <span>Consider both expected returns and probability when making decisions</span>
                    </div>
                    <div className="tip-item warning">
                      <i className="fas fa-exclamation-triangle"></i>
                      <span>Higher returns typically come with higher risk and lower probability</span>
                    </div>
                    <div className="tip-item positive">
                      <i className="fas fa-lightbulb"></i>
                      <span>Regular contributions can significantly boost long-term returns through compounding</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <br/><WaldoAd type="horizontal" />
      {content && content.content}
    </div>
  );
};

export default LumpSumReturnsCalculator;